import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import styled from "styled-components";
import { ProfileContext } from "../contexts/ProfileContext";
import { LoadingSpinner } from "../components/loading-spinner";
import BarchartAreacode from "../components/Barchart-areacode";
import PastoralPagination from "../components/pastoral-care/components/pagination";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import OrganizationNameButton from "../components/organization-name-button";

// ==================== Constants ====================
const DATE_RANGES = {
  ALL: "all",
  YTD: "ytd",
  MONTHLY: "monthly",
  CUSTOM: "custom",
};

const VIEW_MODES = {
  INITIAL: "initial", // Countries
  DRILLDOWN_PROVINCES: "drilldown_provinces", // Provinces
  DRILLDOWN_AREA_CODES: "drilldown_area_codes", // Area Codes
};

const EXPORT_OPTIONS = {
  PRINT: "print",
  EXCEL: "export",
};

const ITEMS_PER_PAGE = 10;

// ==================== SVG Icons ====================
const ChevronDown = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRight = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

// ==================== Helper Functions ====================
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "—";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(" ");
};

// ==================== Main Component ====================
const AreaCodesCalls = () => {
  // ========== COMMON STATE ==========
  const [dataType, setDataType] = useState(DATE_RANGES.YTD);
  const [exportOption, setExportOption] = useState("");
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [selectedRange, setSelectedRange] = useState({ start: "", end: "" });
  const [isLoading, setIsLoading] = useState(false);

  // ========== PAGE-SPECIFIC STATE ==========
  const [areaCodeData, setAreaCodeData] = useState(null);
  const [drilldownData, setDrilldownData] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedProvinceId, setSelectedProvinceId] = useState(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState(null);
  const [previousProvincesData, setPreviousProvincesData] = useState(null);
  const [viewMode, setViewMode] = useState(VIEW_MODES.INITIAL);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAreaCodeChartVisible, setIsAreaCodeChartVisible] = useState(false);
  const [areaCodeDetails, setAreaCodeDetails] = useState([]);

  // ========== REFS ==========
  const selectRef = useRef(null);
  const datePickerRef = useRef(null);
  const componentToPrint = useRef();

  // ========== CONTEXT ==========
  const {
    getAreaCodeStats,
    getProvinceByCountry,
    getAreaCodeByProvince,
    getAreaCodeDetails: fetchAreaCodeDetails,
    worker,
    subscriberAreaCodeChartAllowed,
    organizationFirebaseId,
    organization,
  } = useContext(ProfileContext);

  // ========== DATE HELPERS ==========
  const formatDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;

  const getYTDRange = () => {
    const now = new Date();
    const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
    return { start: formatDate(firstDayOfYear), end: formatDate(now) };
  };

  const getDefaultMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: formatDate(firstDay), end: formatDate(now) };
  };

  const getDateRange = (type, customRange) => {
    if (type === DATE_RANGES.CUSTOM && customRange) {
      return {
        startDate: customRange.start,
        endDate: customRange.end,
      };
    }

    if (type === DATE_RANGES.YTD) {
      const ytd = getYTDRange();
      return {
        startDate: ytd.start,
        endDate: ytd.end,
      };
    }

    if (type === DATE_RANGES.MONTHLY) {
      const monthly = getDefaultMonthRange();
      return {
        startDate: monthly.start,
        endDate: monthly.end,
      };
    }

    return {
      startDate: "2015-01-01",
      endDate: formatDate(new Date()),
    };
  };

  const formatChartData = (response) => {
    if (response?.original?.data && Array.isArray(response.original.data)) {
      const formatted = response.original.data
        .map((item) => {
          if (item.country) {
            return {
              id: item.CountryId,
              label: item.country == 0 ? "Other" : item.country,
              total: item.total,
            };
          } else if (item.province) {
            return {
              id: item.ProvinceId,
              label: item.province,
              total: item.total,
            };
          } else if (item.area_code) {
            return {
              id: item.AreaCodeId,
              label: item.area_code,
              total: item.total,
            };
          }
          return null;
        })
        .filter(Boolean);

      // ✅ SORT BY ID DESC
      formatted.sort((a, b) => b.id - a.id);

      return formatted;
    }

    return [];
  };

  // ========== DATA FETCHING ==========
  const fetchAreaCodeData = async (type = "", startDate = "", endDate = "") => {
    try {
      setIsLoading(true);

      // Check if we have Firebase ID
      if (!organizationFirebaseId) {
        console.error("❌ No Firebase ID available for organization");
        setIsLoading(false);
        return;
      }

      let result;

      if (type === "all") {
        const finalStart = "2015-01-01";
        const finalEnd = formatDate(new Date());
        result = await getAreaCodeStats(
          organizationFirebaseId,
          finalStart,
          finalEnd,
        );
        setSelectedRange({ start: finalStart, end: finalEnd });
      } else if (type === "ytd") {
        const ytd = getYTDRange();
        result = await getAreaCodeStats(
          organizationFirebaseId,
          ytd.start,
          ytd.end,
        );
        setSelectedRange(ytd);
      } else if (type === "monthly") {
        const def = getDefaultMonthRange();
        result = await getAreaCodeStats(
          organizationFirebaseId,
          def.start,
          def.end,
        );
        setSelectedRange(def);
      } else if (type === "custom") {
        if (!startDate || !endDate) {
          console.warn("Custom date range selected but no dates provided");
          return;
        }
        result = await getAreaCodeStats(
          organizationFirebaseId,
          startDate,
          endDate,
        );
        setSelectedRange({ start: startDate, end: endDate });
      } else {
        // Default case - fetch without dates
        result = await getAreaCodeStats(organizationFirebaseId);
        setSelectedRange({ start: "", end: "" });
      }

      console.log("✅ Countries Data Response:", result);
      const formattedData = formatChartData(result);
      console.log("✅ Formatted Chart Data:", formattedData);
      setAreaCodeData(formattedData);
    } catch (error) {
      console.error("❌ Error fetching countries data:", error);
      setAreaCodeData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== INITIAL FETCH ==========
  useEffect(() => {
    // Only fetch if we have Firebase ID
    if (organizationFirebaseId) {
      fetchAreaCodeData("ytd");
    }
  }, [organizationFirebaseId]);

  // ========== PERMISSIONS ==========
  useEffect(() => {
    if (worker?.role === "super_admin" || subscriberAreaCodeChartAllowed) {
      setIsAreaCodeChartVisible(true);
    }
  }, [worker, subscriberAreaCodeChartAllowed]);

  // ========== EXPORT FUNCTIONALITY ==========
  const handlePrint = useReactToPrint({
    content: () => componentToPrint.current,
    documentTitle: `Area_Codes_Report_${selectedRange.start || "all"}_to_${
      selectedRange.end || "all"
    }`,
    onBeforePrint: () => {
      document.body.style.width = "100%";
    },
    onAfterPrint: () => {
      setExportOption("");
      document.body.style.width = "";
    },
  });

  const handleExportExcel = useCallback(() => {
    try {
      const chartData = [];

      // ✅ INITIAL VIEW (Countries)
      if (viewMode === VIEW_MODES.INITIAL && Array.isArray(areaCodeData)) {
        chartData.push(
          ...areaCodeData.map((item) => ({
            Category: "Countries Summary",
            Country: item.label,
            "Call Count": item.total,
          })),
        );
      }

      // ✅ PROVINCES VIEW
      else if (
        viewMode === VIEW_MODES.DRILLDOWN_PROVINCES &&
        Array.isArray(drilldownData)
      ) {
        chartData.push(
          ...drilldownData.map((item) => ({
            Category: "Provinces Summary",
            Province: item.label,
            "Call Count": item.total,
          })),
        );
      }

      // ✅ AREA CODES VIEW
      else if (
        viewMode === VIEW_MODES.DRILLDOWN_AREA_CODES &&
        Array.isArray(drilldownData)
      ) {
        chartData.push(
          ...drilldownData.map((item) => ({
            Category: "Area Codes Summary",
            "Area Code": item.label,
            "Call Count": item.total,
          })),
        );
      }

      // ✅ TABLE DATA (Details)
      const tableData =
        areaCodeDetails?.length > 0
          ? areaCodeDetails.map((item) => ({
              Category: "Area Code Details",
              Time: new Date(item.CalledOn || item.created_at).toLocaleString(
                "en-US",
              ),
              "Types Of Calls": item.CallTypes || item.call_types || "—",
              "Age Group": item.AgeGroup || item.age_range || "—",
              Gender: item.CallerGender || item.gender || "—",
            }))
          : [];

      if (chartData.length === 0 && tableData.length === 0) {
        alert("No data available to export");
        setExportOption("");
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet([...chartData, ...tableData]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "AreaCode_Report");

      const fileName = `Area_Codes_Report_${
        selectedRange.start || "all"
      }_to_${selectedRange.end || "all"}.xlsx`;

      XLSX.writeFile(workbook, fileName);

      setExportOption("");
    } catch (error) {
      console.error("❌ Excel Export Failed:", error);
      alert("Failed to export Excel file");
      setExportOption("");
    }
  }, [viewMode, areaCodeData, drilldownData, areaCodeDetails, selectedRange]);

  useEffect(() => {
    if (exportOption === EXPORT_OPTIONS.PRINT) handlePrint();
    if (exportOption === EXPORT_OPTIONS.EXCEL) handleExportExcel();
  }, [exportOption, handlePrint, handleExportExcel]);

  // ========== FILTER HANDLERS ==========
  const handleFilterChange = (type) => {
    setDataType(type);
    if (type === "custom") {
      setShowDatePicker(true);
    } else {
      fetchAreaCodeData(type);
      resetToInitialView();
    }
  };

  const handleApplyCustomDates = () => {
    if (!tempStartDate || !tempEndDate) {
      alert("Select both dates.");
      return;
    }
    setDataType("custom");
    setShowDatePicker(false);
    fetchAreaCodeData("custom", tempStartDate, tempEndDate);
    resetToInitialView();
  };

  const handleCancelCustomDates = () => {
    setTempStartDate("");
    setTempEndDate("");
    setShowDatePicker(false);
  };

  // ========== PAGE-SPECIFIC HANDLERS ==========
  const resetToInitialView = useCallback(() => {
    setViewMode(VIEW_MODES.INITIAL);
    setDrilldownData(null);
    setSelectedCountry(null);
    setSelectedCountryId(null);
    setSelectedProvince(null);
    setSelectedProvinceId(null);
    setSelectedAreaCode(null);
    setPreviousProvincesData(null);
    setIsBreakdownOpen(false);
    setBreadcrumbs([]);
    setCurrentPage(1);
  }, []);

  const handleBreadcrumbClick = async (index) => {
    console.log("🔍 Breadcrumb clicked:", { index, breadcrumbs, viewMode });

    if (index === 0) {
      resetToInitialView();
    } else if (index === 1 && viewMode === VIEW_MODES.DRILLDOWN_AREA_CODES) {
      try {
        setIsLoading(true);
        setViewMode(VIEW_MODES.DRILLDOWN_PROVINCES);
        setSelectedProvince(null);
        setSelectedProvinceId(null);
        setSelectedAreaCode(null);
        setIsBreakdownOpen(false);

        setBreadcrumbs([
          { label: "All Countries", clickable: true },
          { label: `Country: ${selectedCountry}`, clickable: false },
        ]);

        if (previousProvincesData) {
          console.log("🔍 Using cached provinces data");
          setDrilldownData(previousProvincesData);
        } else {
          console.log("🔍 Refetching provinces data");
          const { startDate, endDate } = getDateRange(dataType, selectedRange);
          const response = await getProvinceByCountry(
            organizationFirebaseId,
            startDate,
            endDate,
            selectedCountry,
            selectedCountryId,
          );
          console.log("✅ Refetched Provinces Data:", response);
          const formattedData = formatChartData(response);
          setDrilldownData(formattedData);
        }
      } catch (error) {
        console.error("❌ Error navigating back to provinces:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCountryClick = useCallback(
    async (countryLabel, countryId) => {
      if (!countryId) return;

      try {
        setIsLoading(true);
        setSelectedCountry(countryLabel);
        setSelectedCountryId(countryId);
        setViewMode(VIEW_MODES.DRILLDOWN_PROVINCES);

        setBreadcrumbs([
          { label: "All Countries", clickable: true },
          { label: `Country: ${countryLabel}`, clickable: false },
        ]);

        const { startDate, endDate } = getDateRange(dataType, selectedRange);

        const response = await getProvinceByCountry(
          organizationFirebaseId,
          startDate,
          endDate,
          countryId, // ✅ ID sent to API
        );

        const formattedData = formatChartData(response);
        setDrilldownData(formattedData);
        setPreviousProvincesData(formattedData);
      } catch (error) {
        console.error("Error fetching provinces:", error);
        setDrilldownData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [dataType, selectedRange, organizationFirebaseId],
  );

  const handleProvinceClick = useCallback(
    async (provinceLabel, provinceId) => {
      if (!provinceId) return;

      try {
        setIsLoading(true);
        setSelectedProvince(provinceLabel);
        setSelectedProvinceId(provinceId);
        setViewMode(VIEW_MODES.DRILLDOWN_AREA_CODES);

        setBreadcrumbs([
          { label: "All Countries", clickable: true },
          { label: `Country: ${selectedCountry}`, clickable: true },
          { label: `Province: ${provinceLabel}`, clickable: false },
        ]);

        const { startDate, endDate } = getDateRange(dataType, selectedRange);

        const response = await getAreaCodeByProvince(
          organizationFirebaseId,
          startDate,
          endDate,
          selectedCountryId, // ✅ PASS COUNTRY ID
          provinceId, // ✅ PASS PROVINCE ID
        );

        const formattedData = formatChartData(response);
        setDrilldownData(formattedData);
      } catch (error) {
        console.error("Error fetching area codes:", error);
        setDrilldownData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      dataType,
      selectedRange,
      selectedCountry,
      selectedCountryId,
      organizationFirebaseId,
    ],
  );

  const handleAreaCodeClick = useCallback(
    async (areaCode) => {
      console.log("🔍 handleAreaCodeClick called with:", {
        areaCode,
        areaCodeType: typeof areaCode,
        isArray: Array.isArray(areaCode),
      });

      const areaCodeString = Array.isArray(areaCode)
        ? areaCode[0] || ""
        : areaCode;

      // Extract just the area code without the + sign for display
      const displayAreaCode = areaCodeString.replace("+", "");

      try {
        setSelectedAreaCode(displayAreaCode);
        setCurrentPage(1);
        setIsLoading(true);

        if (!isBreakdownOpen) {
          setIsBreakdownOpen(true);
        }

        const { startDate, endDate } = getDateRange(dataType, selectedRange);

        console.log("🔍 Fetching details for:", {
          areaCode: areaCodeString,
          countryId: selectedCountryId,
          provinceId: selectedProvinceId,
          organizationFirebaseId,
          dataType,
          startDate,
          endDate,
        });

        const details = await fetchAreaCodeDetails(
          areaCodeString,
          organizationFirebaseId,
          selectedCountryId,
          selectedProvinceId,
          {
            start: startDate,
            end: endDate,
          },
          dataType,
        );

        setAreaCodeDetails(details || []);
      } catch (error) {
        console.error("❌ Error fetching area code details:", error);
        setAreaCodeDetails([]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      dataType,
      selectedRange,
      isBreakdownOpen,
      fetchAreaCodeDetails,
      organizationFirebaseId,
      selectedCountryId,
      selectedProvinceId,
    ],
  );

  // ========== UI HANDLERS ==========
  const toggleBreakdown = () => {
    setIsBreakdownOpen(!isBreakdownOpen);
  };

  // ========== SELECT & DATEPICKER EFFECTS ==========
  useEffect(() => {
    const selectElement = selectRef.current;
    if (!selectElement) return;

    const handleMouseDown = () => setIsSelectOpen((prev) => !prev);
    const handleBlur = () => setIsSelectOpen(false);

    selectElement.addEventListener("mousedown", handleMouseDown);
    selectElement.addEventListener("blur", handleBlur);

    return () => {
      selectElement.removeEventListener("mousedown", handleMouseDown);
      selectElement.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDatePicker]);

  // ========== COMPUTED VALUES ==========
  const paginatedDetails = areaCodeDetails?.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const getBarClickHandler = () => {
    switch (viewMode) {
      case VIEW_MODES.INITIAL:
        return (item, itemId, itemName) => {
          handleCountryClick(item, itemId, itemName);
        };
      case VIEW_MODES.DRILLDOWN_PROVINCES:
        return (item, itemId, itemName) => {
          handleProvinceClick(item, itemId, itemName);
        };
      case VIEW_MODES.DRILLDOWN_AREA_CODES:
        return handleAreaCodeClick;
      default:
        return handleCountryClick;
    }
  };

  const getEmptyMessage = () => {
    switch (viewMode) {
      case VIEW_MODES.INITIAL:
        return "No countries data available";
      case VIEW_MODES.DRILLDOWN_PROVINCES:
        return "No provinces data available";
      case VIEW_MODES.DRILLDOWN_AREA_CODES:
        return "No area codes data available";
      default:
        return "No data available";
    }
  };

  return (
    <Wrapper>
      {/* ========== STANDARDIZED TOP SECTION ========== */}
      <TopSection>
        {(worker && worker.role === "subscriber") ||
        organization?.name?.toLowerCase() === "crossroads" ? null : (
          <OrganizationNameButton />
        )}

        <TitleText>Calls by Area Codes</TitleText>
      </TopSection>

      {/* ========== STANDARDIZED TOGGLE FILTERS ========== */}
      <ToggleButtonContainer>
        <ToggleContent>
          <ToggleButtonGroup>
            <ToggleButton
              active={dataType === DATE_RANGES.YTD}
              onClick={() => handleFilterChange("ytd")}
            >
              YEAR TO DATE
            </ToggleButton>

            <ToggleButton
              active={dataType === DATE_RANGES.ALL}
              onClick={() => handleFilterChange("all")}
            >
              ALL DATA
            </ToggleButton>

            <ToggleButton
              active={dataType === DATE_RANGES.MONTHLY}
              onClick={() => handleFilterChange("monthly")}
            >
              CURRENT MONTH
            </ToggleButton>

            <ToggleButton
              active={dataType === DATE_RANGES.CUSTOM}
              onClick={() => {
                setDataType("custom");
                setShowDatePicker(true);
              }}
            >
              CUSTOM DATE RANGE
            </ToggleButton>
          </ToggleButtonGroup>

          <ExportContainer>
            <SelectWrapper>
              <ExportSelect
                ref={selectRef}
                value={exportOption}
                onChange={(e) => setExportOption(e.target.value)}
              >
                <option value="" disabled>
                  Export Options
                </option>
                <option value="print">Print PDF</option>
                <option value="export">Export as Excel Sheet</option>
              </ExportSelect>
              <ArrowIcon isOpen={isSelectOpen}>
                <ChevronDown />
              </ArrowIcon>
            </SelectWrapper>
          </ExportContainer>
        </ToggleContent>

        {showDatePicker && (
          <DatePickerDropdown ref={datePickerRef}>
            <DateInputGroup>
              <label>From</label>
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
              />
            </DateInputGroup>

            <DateInputGroup>
              <label>To</label>
              <input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
              />
            </DateInputGroup>

            <DatePickerActions>
              <button onClick={handleApplyCustomDates}>Apply</button>
              <button onClick={handleCancelCustomDates}>Cancel</button>
            </DatePickerActions>
          </DatePickerDropdown>
        )}
      </ToggleButtonContainer>

      {/* ========== PAGE-SPECIFIC CONTENT ========== */}
      <printContainer ref={componentToPrint}>
        {/* ========== HIDDEN PDF-ONLY TITLE ========== */}
        <PdfOnlyHeader>
          <div className="main-title">CALLS BY AREA CODES</div>
        </PdfOnlyHeader>

        <ContentWrapper>
          <ChartContainer>
            {breadcrumbs.length > 0 && (
              <BreadcrumbsContainer>
                {breadcrumbs.map((breadcrumb, index) => (
                  <BreadcrumbItem key={index}>
                    {breadcrumb.clickable ? (
                      <BreadcrumbLink
                        onClick={() => handleBreadcrumbClick(index)}
                      >
                        {breadcrumb.label}
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbText>{breadcrumb.label}</BreadcrumbText>
                    )}
                    {index < breadcrumbs.length - 1 && (
                      <BreadcrumbSeparator>/</BreadcrumbSeparator>
                    )}
                  </BreadcrumbItem>
                ))}
              </BreadcrumbsContainer>
            )}

            <ChartBox>
              {isLoading ? (
                <SpinnerWrapper>
                  <LoadingSpinner />
                </SpinnerWrapper>
              ) : (viewMode === VIEW_MODES.INITIAL &&
                  areaCodeData &&
                  Object.keys(areaCodeData).length > 0) ||
                (viewMode === VIEW_MODES.DRILLDOWN_PROVINCES &&
                  drilldownData &&
                  Object.keys(drilldownData).length > 0) ||
                (viewMode === VIEW_MODES.DRILLDOWN_AREA_CODES &&
                  drilldownData &&
                  Object.keys(drilldownData).length > 0) ? (
                <BarchartAreacode
                  data={
                    viewMode === VIEW_MODES.INITIAL
                      ? areaCodeData
                      : drilldownData
                  }
                  inlineMode={true}
                  onBarClick={getBarClickHandler()}
                  viewMode={viewMode}
                />
              ) : (
                <Message>{getEmptyMessage()}</Message>
              )}
            </ChartBox>
          </ChartContainer>
        </ContentWrapper>

        <BreakdownCard isOpen={isBreakdownOpen}>
          <BreakdownTitle onClick={toggleBreakdown}>
            <ChevronIcon>
              {isBreakdownOpen ? <ChevronDown /> : <ChevronRight />}
            </ChevronIcon>
            AREA CODE BREAKDOWN
          </BreakdownTitle>

          {isBreakdownOpen && (
            <BreakdownContent>
              <BreakdownNote>
                {selectedAreaCode
                  ? `Area Code: ${selectedAreaCode}`
                  : "Click on an area code in the chart above to view details"}
              </BreakdownNote>

              {isLoading ? (
                <EmptyState>
                  <LoadingSpinner />
                </EmptyState>
              ) : areaCodeDetails && areaCodeDetails.length > 0 ? (
                <>
                  <TableWrapper>
                    <TableContainer>
                      <StyledTable>
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Types Of Calls</th>
                            <th>Age Group</th>
                            <th>Gender</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedDetails?.length ? (
                            paginatedDetails.map((item, index) => (
                              <tr key={item.id || index}>
                                <td>
                                  {new Date(
                                    item.CalledOn || item.created_at,
                                  ).toLocaleString("en-US", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                    hour12: true,
                                  })}
                                </td>
                                <td>
                                  {item.CallTypes || item.call_types || "—"}
                                </td>
                                <td className="text-capitalize">
                                  {item.AgeGroup || item.age_range || "—"}
                                </td>
                                <td className="text-capitalize">
                                  {item.CallerGender || item.gender || "—"}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan="4"
                                style={{ textAlign: "center", padding: "20px" }}
                              >
                                No call records found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </StyledTable>
                    </TableContainer>
                  </TableWrapper>

                  <StyledPagination>
                    <PastoralPagination
                      currentPage={currentPage}
                      itemsPerPage={ITEMS_PER_PAGE}
                      total_count={areaCodeDetails?.length || 0}
                      handlePageChange={setCurrentPage}
                    />
                  </StyledPagination>
                </>
              ) : (
                <EmptyState>
                  {selectedAreaCode ? (
                    <Message>No data available for this area code</Message>
                  ) : (
                    <Message>Select an area code to view details</Message>
                  )}
                </EmptyState>
              )}
            </BreakdownContent>
          )}
        </BreakdownCard>
      </printContainer>
    </Wrapper>
  );
};

export default AreaCodesCalls;

// ========== STYLED COMPONENTS ==========

// Standardized components from the example
const PdfOnlyHeader = styled.div`
  display: none;

  @media print {
    display: block;
    text-align: center;
    margin: 40px 0;
    padding-bottom: 20px;
    font-family: "Helvetica", "Arial", sans-serif;

    .main-title {
      text-align: center;
      font-family: Recoleta, serif;
      font-size: 1.25rem;
      font-weight: bold;
      color: #ec3310;
      margin-bottom: 0;
      text-decoration: underline;
      grid-column: 2;

      @media (max-width: 768px) {
        grid-column: 1;
        font-size: 1.1rem;
      }

      @media (max-width: 480px) {
        font-size: 1rem;
      }
    }
  }
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.75rem;
  }
`;

const ToggleButtonContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: start;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
    padding: 0 1rem;
  }
`;

const ToggleContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  background: #eeeeee;
  border: 1px solid #e5e7eb;
  border-radius: 62px;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    border-radius: 12px;
    max-width: 300px;
    width: 100%;
  }

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const ExportContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const ToggleButton = styled.button`
  width: auto;
  min-width: 164px;
  height: 29px;
  padding: 0 12px;

  font-family: "Futura PT", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0.15em;
  text-align: center; /* Center alignment */

  border: none;
  border-radius: 62px;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${({ active }) => (active ? "#2DB3C0" : "transparent")};
  color: ${({ active }) => (active ? "white" : "#86CCDA")};
  box-shadow: ${({ active }) =>
    active ? "0 2px 6px rgba(0, 0, 0, 0.15)" : "none"};

  white-space: nowrap;

  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    height: 40px;

    font-size: 14px; /* Keep typography consistent */
    letter-spacing: 0.15em; /* Keep consistent */
    border-radius: 8px;
    margin: 2px 0;
  }

  @media (max-width: 480px) {
    height: 36px;

    font-size: 14px; /* Maintain required size */
    letter-spacing: 0.25em; /* Maintain required spacing */

    padding: 0 8px;
  }
`;

const DatePickerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 280px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 480px) {
    min-width: 260px;
    padding: 12px;
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;

  label {
    font-size: 13px;
    color: #333;
    font-weight: 500;
    font-family: "Space Grotesk";
  }

  input {
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    font-size: 13px;
    font-family: "Space Grotesk";
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #2db3c0;
      box-shadow: 0 0 0 2px rgba(45, 179, 192, 0.1);
    }
  }
`;

const DatePickerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;

  button {
    flex: 1;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-family: "Space Grotesk";
    font-weight: 600;
    border: none;
    transition: all 0.2s;

    &:first-child {
      background-color: #2db3c0;
      color: white;

      &:hover {
        background-color: #249aa5;
      }
    }

    &:last-child {
      background-color: #f3f4f6;
      color: #666;

      &:hover {
        background-color: #e5e7eb;
      }
    }
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 172px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 200px;
  }
`;

const ExportSelect = styled.select`
  appearance: none;
  background: #faf8f8;
  border: 1px solid #106271;
  color: #106271;
  font-family: "Space Grotesk";
  border-radius: 8px;
  width: 100%;
  height: 29px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  padding: 0 10px;

  &:focus {
    outline: none;
  }
`;

const ArrowIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%)
    rotate(${({ isOpen }) => (isOpen ? "180deg" : "0deg")});
  color: #6b7280;
  pointer-events: none;
  transition: transform 0.3s ease;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const TitleText = styled.h2`
  text-align: center;
  font-family: Recoleta, serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ec3310;
  margin-bottom: 0;
  text-decoration: underline;
  grid-column: 2;

  @media (max-width: 768px) {
    grid-column: 1;
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const printContainer = styled.div`
  width: 100%;
  display: block;
  @media print {
    width: 100%;
    display: block;
  }
`;

// Your existing styled components (keep as is)
const Wrapper = styled.div`
  width: 100%;
  padding: 2rem;
  background-color: #f3f9fa;
  min-height: 100vh;

  @media (max-width: 1024px) {
    padding: 1.5rem 0;
  }

  @media (max-width: 768px) {
    padding: 1rem 0;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0;
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const Message = styled.p`
  padding: 20px;
  text-align: center;
  color: #555;
  font-size: 16px;

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 15px;
  }
`;

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const ChartBox = styled.div`
  width: 100%;
  padding: 24px;
  background: white;
  border-radius: 16px;
  border: 1px solid #f3f4f6;
  min-height: 400px;

  @media (max-width: 768px) {
    padding: 16px;
    min-height: 350px;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    min-height: 300px;
    border-radius: 8px;
  }
`;

const BreadcrumbsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 0 16px;
  flex-wrap: wrap;
  justify-content: end;

  @media (max-width: 768px) {
    padding: 0 12px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    padding: 0 8px;
    margin-bottom: 8px;
    gap: 6px;
  }
`;

const BreadcrumbItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbLink = styled.span`
  color: #2db3c0;
  cursor: pointer;
  font-family: "Space Grotesk";
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: #249aa5;
    text-decoration: underline;
  }

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const BreadcrumbText = styled.span`
  color: #666;
  font-family: "Space Grotesk";
  font-size: 14px;
  font-weight: 500;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #999;
  font-family: "Space Grotesk";
  font-size: 14px;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const BreakdownCard = styled.div`
  width: 100%;
  min-height: ${({ isOpen }) => (isOpen ? "200px" : "auto")};
  margin: 2.5rem auto 0 auto;
  background: #f1f1f1;
  border-radius: 16px;
  border: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  padding: ${({ isOpen }) => (isOpen ? "20px" : "16px 20px")};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    margin: 2rem auto 0 auto;
    padding: ${({ isOpen }) => (isOpen ? "16px" : "14px 16px")};
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    margin: 1.5rem auto 0 auto;
    padding: ${({ isOpen }) => (isOpen ? "12px" : "10px 12px")};
    border-radius: 8px;
    min-height: ${({ isOpen }) => (isOpen ? "180px" : "auto")};
  }
`;

const BreakdownTitle = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  font-weight: 700; /* Bold */
  font-style: normal;
  line-height: 100%;
  color: #2db3c0;
  letter-spacing: 0.25em; /* 25% spacing */
  text-align: center;

  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #249aa5;
  }

  @media (max-width: 480px) {
    font-size: 14px; /* Keeping typography consistent */
    letter-spacing: 0.25em; /* Keeping consistent */
  }
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BreakdownContent = styled.div`
  margin-top: 1rem;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const BreakdownNote = styled.p`
  font-size: 12px;
  color: #666;
  font-weight: 500;
  font-family: "Space Grotesk";
  text-align: center;
  margin: 0 0 20px 0;

  @media (max-width: 480px) {
    font-size: 11px;
    margin: 0 0 16px 0;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow: hidden;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin-bottom: 20px;
  max-height: 600px;

  @media (max-width: 768px) {
    max-height: 500px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    max-height: 400px;
    margin-bottom: 12px;
    border-radius: 6px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  font-family: "Space Grotesk", sans-serif;
  border-collapse: collapse;
  min-width: 800px;
  background-color: #fff;

  th,
  td {
    text-align: center;
    padding: 12px;
    border-bottom: 1px solid #dee2e6;
    white-space: nowrap;
  }

  thead th {
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8f9fa;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }

  .text-capitalize {
    text-transform: capitalize;
  }

  @media (max-width: 1024px) {
    min-width: 700px;

    th,
    td {
      padding: 10px;
      font-size: 14px;
    }
  }

  @media (max-width: 768px) {
    min-width: 600px;

    th,
    td {
      padding: 8px;
      font-size: 13px;
    }
  }

  @media (max-width: 480px) {
    min-width: 500px;

    th,
    td {
      padding: 6px;
      font-size: 12px;
    }
  }
`;

const StyledPagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding-top: 10px;

  @media (max-width: 480px) {
    padding-top: 8px;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-family: "Space Grotesk";
  font-size: 14px;
  text-align: center;
  padding: 40px 20px;

  @media (max-width: 480px) {
    font-size: 13px;
    padding: 30px 15px;
  }
`;
